import { Time } from '@gonfalon/datetime/Time';
import { DateFormat } from '@gonfalon/format/constants';
import { msg } from '@gonfalon/messages/msg';
import type { schemas } from '@gonfalon/openapi/types';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';

import { CodeSpan } from '../../CodeSpan';

import { EventContextKinds } from './EventContextKinds';

export type EventPreviewColumn = {
  key: string;
  label: string;
  value: (event: schemas['MetricEventDefinitionInstance']) => React.ReactNode;
};

/* takes JSON object and returns a truncated string with the first property of the object & an ellipsis */
const getTruncatedJSON = (jsonData: unknown): string | null => {
  if (!jsonData || typeof jsonData !== 'object') {
    return null;
  }

  const dataStr = JSON.stringify(jsonData);
  if (dataStr.length === 0 || dataStr === '{}') {
    return dataStr || '{}';
  }

  // Handle arrays differently from objects
  if (Array.isArray(jsonData)) {
    return `[${JSON.stringify(jsonData[0])}, ...]`;
  }

  // For objects, find the first property safely
  const firstCommaIndex = dataStr.indexOf(',');
  const firstProperty = firstCommaIndex > -1 ? dataStr.slice(0, firstCommaIndex) : dataStr.slice(0, -1); // Remove closing }

  // Find the first colon after the opening brace
  const colonIndex = firstProperty.indexOf(':', 1); // Start from index 1 to skip opening brace
  if (colonIndex === -1) {
    // No colon found, might be malformed or edge case
    return `${firstProperty}...`;
  }

  const key = firstProperty.slice(0, colonIndex);
  const value = firstProperty.slice(colonIndex + 1);

  // Safely truncate the value - if the value is longer than 5 characters, truncate it to 5 characters and add an ellipsis
  const truncatedValue = value.length > 5 ? `${value.slice(0, 5)}...` : value;
  return `${key}: ${truncatedValue}`;
};

export enum EventKind {
  FEATURE = 'feature',
  CLICK = 'click',
  PAGEVIEW = 'pageview',
  CUSTOM = 'custom',
  IDENTIFY = 'identify',
  INDEX = 'index',
  SUMMARY = 'summary',
  FLATTEN_SUMMARY = 'flattenSummary',
  DEBUG = 'debug',
  UNKNOWN = 'unknown',
}

const eventPreviewColumns = (): Record<string, EventPreviewColumn> => ({
  timestamp: {
    key: 'timestamp',
    label: msg('metrics.eventDataPreview.timestampColumn'),
    value: (event: schemas['MetricEventDefinitionInstance']) => (
      <Time datetime={event.eventTimeMs} dateFormat={DateFormat.YYYY_MM_DD_HH_MM} notooltip includeTimeZone />
    ),
  },
  eventKey: {
    key: 'eventKey',
    label: msg('metrics.eventDataPreview.eventKeyColumn'),
    value: (event: schemas['MetricEventDefinitionInstance']) => event.eventKey,
  },
  contextKinds: {
    key: 'contextKinds',
    label: msg('metrics.eventDataPreview.contextKindColumn'),
    value: (event: schemas['MetricEventDefinitionInstance']) => <EventContextKinds contexts={event.contexts} />,
  },
  source: {
    key: 'source',
    label: msg('metrics.eventDataPreview.sourceColumn'),
    value: (event: schemas['MetricEventDefinitionInstance']) =>
      (event.requestDetail as { event_source?: string })?.event_source,
  },
  data: {
    key: 'eventDetails',
    label: msg('metrics.eventDataPreview.eventDetailsColumn'),
    value: (event: schemas['MetricEventDefinitionInstance']) => {
      const truncatedJSON = getTruncatedJSON(event);
      return truncatedJSON ? <CodeSpan textValue={truncatedJSON} /> : null;
    },
  },
  value: {
    key: 'value',
    label: msg('metrics.eventDataPreview.valueColumn'),
    value: (event: schemas['MetricEventDefinitionInstance']) => event.value,
  },
  url: {
    key: 'url',
    label: msg('metrics.eventDataPreview.pageUrlColumn'),
    value: (event: schemas['MetricEventDefinitionInstance']) => <TextTruncator>{event.url}</TextTruncator>,
  },
  selector: {
    key: 'selector',
    label: msg('metrics.eventDataPreview.selectorColumn'),
    value: (event: schemas['MetricEventDefinitionInstance']) => event.selector,
  },
});

/* Note: don't directly add columns to this object, add them to eventPreviewColumns instead */
const eventPreviewColumnsByEventKind = (): Record<string, EventPreviewColumn[]> => {
  const columns = eventPreviewColumns();
  return {
    [EventKind.CUSTOM]: [columns.timestamp, columns.contextKinds, columns.value, columns.data],
    [EventKind.PAGEVIEW]: [columns.timestamp, columns.contextKinds, columns.value, columns.data],
    [EventKind.CLICK]: [columns.timestamp, columns.contextKinds, columns.value, columns.data],
  };
};

export const getEventPreviewTableData = (eventKind: string, _isNumeric?: boolean) => {
  const columnsForKind = eventPreviewColumnsByEventKind()[eventKind];
  if (!columnsForKind) {
    return [];
  }
  return columnsForKind;
};
